import { gsap } from "gsap"

export default class CustomCursor {
  constructor() {
    this.cursor = null
    this.cursorContainer = null
    this.isDesktop = false
    this.initialPosition = true
    this.mouse = { x: 0, y: 0 }
    this.cursorPos = { x: 0, y: 0 }
    this.mediaQuery = null
    this.animationId = null
    this.isInitialized = false
    this.isVisible = true
    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handleMouseOut = this.handleMouseOut.bind(this)
    this.handleMouseEnter = this.handleMouseEnter.bind(this)
    this.handleFullscreenEnter = this.handleFullscreenEnter.bind(this)
    this.handleFullscreenExit = this.handleFullscreenExit.bind(this)

    this.init()
  }

  init() {
    this.cursorContainer = document.querySelector("[data-module-custom-cursor]")
    this.cursor = document.querySelector("[data-cursor-element]")

    if (!this.cursor || !this.cursorContainer) return

    this.mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)")
    this.checkDeviceType()

    this.bindEvents()

    window.H.on("NAVIGATE_IN", () => {
      requestAnimationFrame(() => {
        this.bindHoverEvents()
        this.bindMenuEvents()
      })
    })

    this.isInitialized = true
  }

  checkDeviceType() {
    this.isDesktop = this.mediaQuery.matches

    if (this.isDesktop) {
      this.showCustomCursor()
      this.animate()
    } else {
      this.hideCustomCursor()
    }
  }

  showCustomCursor() {
    this.cursorContainer.style.display = "block"
    document.documentElement.classList.add("has-custom-cursor")

    
    // Only show cursor if window is visible
    if (this.isVisible) {
      gsap.set(this.cursorContainer, {
        opacity: 1,
      })
      gsap.set(this.cursor, {
        opacity: 1,
      })
    }
  }

  hideCustomCursor() {
    this.cursorContainer.style.display = "none"
    document.documentElement.classList.remove("has-custom-cursor")
    // Restore default cursor
    document.body.style.cursor = "auto"
    // Stop animation loop
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  bindEvents() {
    // Mouse move event
    document.addEventListener("mousemove", this.handleMouseMove)

    // Window resize event to recheck device type
    window.addEventListener("resize", this.handleResize)

    // Mouse out event to hide cursor when mouse leaves the viewport
    document.addEventListener("mouseout", this.handleMouseOut)
    document.addEventListener("mouseover", this.handleMouseEnter)
    
    // Fallback for Safari - use visibility API
    document.addEventListener("visibilitychange", this.handleVisibilityChange.bind(this))

    // Media query change event
    this.mediaQuery.addEventListener("change", () => {
      this.checkDeviceType()
    })

    // Project player fullscreen events
    document.addEventListener('player:enterfullscreen', this.handleFullscreenEnter)
    document.addEventListener('player:exitfullscreen', this.handleFullscreenExit)

    // Hover events for interactive elements
    this.bindHoverEvents()
    
    // Menu events
    this.bindMenuEvents()

    window.H.on("NAVIGATE_OUT", () => {
        this.cursor.classList.add('bg-c')
        this.cursor.classList.remove('bg-bg')
      });
    
  }

  handleMouseMove(e) {
    this.mouse.x = e.clientX
    this.mouse.y = e.clientY
    
    // If mouse just re-entered the browser, show cursor
    if (!this.isVisible && this.isDesktop) {
      this.isVisible = true
      gsap.set(this.cursorContainer, {
        opacity: 1,
      })
      gsap.set(this.cursor, {
        opacity: 1,
      })
      
      // Restart animation loop if it was stopped
      if (!this.animationId) {
        this.animate()
      }
    }
  }
  
  handleVisibilityChange() {
    // When document becomes visible again, check if we should show cursor
    if (!document.hidden && this.isDesktop) {
      // Only show if mouse is actually in the document
      if (this.mouse.x !== 0 && this.mouse.y !== 0) {
        this.isVisible = true
        gsap.set(this.cursorContainer, {
          opacity: 1,
        })
        gsap.set(this.cursor, {
          opacity: 1,
        })
        
        if (!this.animationId) {
          this.animate()
        }
      }
    }
  }

  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.checkDeviceType()
    }, 100)
  }

  handleMouseOut(event) {
    // If the mouse leaves the window, relatedTarget will be null
    if (!event.relatedTarget && !event.toElement) {
      this.isVisible = false
      
      if (this.isDesktop) {
        // Hide cursor when mouse leaves the viewport
        gsap.set(this.cursorContainer, {
          opacity: 0,
        })
        gsap.set(this.cursor, {
          opacity: 0,
        })
        
        // Stop animation loop to save resources
        if (this.animationId) {
          cancelAnimationFrame(this.animationId)
          this.animationId = null
        }
      }
    }
  }

  handleMouseEnter(event) {
    this.isVisible = true
    
    if (this.isDesktop) {
      // Show cursor when mouse comes back into viewport
      gsap.set(this.cursorContainer, {
        opacity: 1,
      })
      gsap.set(this.cursor, {
        opacity: 1,
      })
      
      // Restart animation loop when mouse re-enters
      if (!this.animationId) {
        this.animate()
      }
    }
  }

  bindHoverEvents() {
    const interactiveElements = document.querySelectorAll(
      'a, button, [data-cursor-hover], input[type="button"], input[type="range"], input[type="submit"]'
    )

    interactiveElements.forEach((element) => {
      element.style.cursor = "none"

      element.addEventListener("mouseenter", () => {
        this.onHover()
      })

      element.addEventListener("mouseleave", () => {
        this.onLeave()
      })
    })

  }

  bindMenuEvents() {
    // Handle menu wrapper hover for background change
    const menuWrapper = document.querySelector('[data-menu-wrapper]')
   
    if (menuWrapper) {
      menuWrapper.style.cursor = "none"

      menuWrapper.addEventListener("mouseenter", () => {
        this.onMenuHover()
      })

      menuWrapper.addEventListener("mouseleave", () => {
        this.onMenuLeave()
      })
    }
  }

  rebindHoverEvents() {
    // Remove existing hover event listeners and rebind for new page content
    this.bindHoverEvents()
    this.bindMenuEvents()
  }

  onHover() {
    gsap.to(this.cursor, {
      scale: 1.5,
      duration: 0.3,
      ease: "power2.out",
    })
  }

  onLeave() {
    gsap.to(this.cursor, {
      scale: 1,
      duration: 0.3,
      ease: "power2.out",
    })
  }

  onMenuHover() {
    // Change cursor background from bg-c to bg-bg when hovering menu wrapper
    this.cursor.classList.remove('bg-c')
    this.cursor.classList.add('bg-bg')
  }

  onMenuLeave() {
    // Change cursor background back from bg-bg to bg-c when leaving menu wrapper
    this.cursor.classList.remove('bg-bg')
    this.cursor.classList.add('bg-c')
  }

  handleFullscreenEnter() {
    // Hide custom cursor when entering fullscreen
    if (this.isDesktop) {
      document.documentElement.classList.remove('has-custom-cursor');
      gsap.to(this.cursorContainer, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.out",
      })
      
      // Stop animation loop to save resources
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
        this.animationId = null
      }
    }
  }

  handleFullscreenExit() {
    // Show custom cursor when exiting fullscreen
    if (this.isDesktop && this.isVisible) {
      document.documentElement.classList.add('has-custom-cursor');
      gsap.to(this.cursorContainer, {
        opacity: 1,
        duration: 0.3,
        ease: "power2.out",
      })
      
      // Restart animation loop
      if (!this.animationId) {
        this.animate()
      }
    }
  }

  animate() {
    if (!this.isDesktop || !this.isVisible) return

    if (this.mouse.x === 0 && this.mouse.y === 0) {
      gsap.set(this.cursorContainer, {
        opacity: 0,
      })
      gsap.set(this.cursor, {
        opacity: 0,
      })
    } else {
      gsap.set(this.cursorContainer, {
        opacity: 1,
      })
      gsap.set(this.cursor, {
        opacity: 1,
      })
    }

    if (this.initialPosition && this.mouse.x !== 0 && this.mouse.y !== 0) {
      gsap.set(this.cursor, {
        x: this.mouse.x,
        y: this.mouse.y,
      })
      this.initialPosition = false
    } else {
      gsap.to(this.cursorPos, {
        x: this.mouse.x,
        y: this.mouse.y,
        duration: 0.1,
        ease: "power2.out",
      })
      gsap.set(this.cursor, {
        x: this.cursorPos.x,
        y: this.cursorPos.y,
      })
    }

    this.animationId = requestAnimationFrame(() => this.animate())
  }

  destroy() {
    // Clean up event listeners
    document.removeEventListener("mousemove", this.handleMouseMove)
    window.removeEventListener("resize", this.handleResize)
    document.removeEventListener("mouseout", this.handleMouseOut)
    document.removeEventListener("mouseover", this.handleMouseEnter)
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
    document.removeEventListener('player:enterfullscreen', this.handleFullscreenEnter)
    document.removeEventListener('player:exitfullscreen', this.handleFullscreenExit)

    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.checkDeviceType)
    }

    // Restore default cursor
    document.body.style.cursor = "auto"

    // Clear any pending timeouts
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }

    // Stop animation loop
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }

    this.isInitialized = false
  }
}
